import { useState } from 'react';
import { api } from '../lib/api';

interface ForgotPasswordPageProps {
  onNavigate: (page: 'login') => void;
}

export function ForgotPasswordPage({ onNavigate }: ForgotPasswordPageProps) {
  const [email, setEmail] = useState('');
  const [orgSlug, setOrgSlug] = useState(getOrgFromUrl());
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.post('/auth/forgot-password', {
        email,
        organizationSlug: orgSlug,
      });
    } catch {
      // Always show success to prevent user enumeration
    } finally {
      setIsLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-with-text.svg" alt="OpenShul" className="h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Reset Password</h1>
          <p className="mt-2 text-gray-600">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {submitted ? (
            <div className="text-center">
              <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700 mb-6">
                If an account exists with that email, a password reset link has been sent.
                Please check your inbox.
              </div>
              <button
                onClick={() => onNavigate('login')}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="orgSlug" className="block text-sm font-medium text-gray-700">
                  Organization
                </label>
                <input
                  id="orgSlug"
                  type="text"
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(e.target.value)}
                  placeholder="your-synagogue"
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <button
              onClick={() => onNavigate('login')}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function getOrgFromUrl(): string {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const subdomain = parts[0];
  if (parts.length > 2 && subdomain && subdomain !== 'www' && subdomain !== 'staging' && subdomain !== 'portal') {
    return subdomain;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('org') ?? '';
}
